import { ReactNode, useEffect, useMemo, useState } from "react";
import { Link, NavLink, matchPath, useLocation } from "react-router-dom";
import { ChevronRight, X } from "lucide-react";
import { spaAssetBase, type SessionPayload } from "../api";
import { spaPathFromBackendHref } from "../navPaths";
import { classNames } from "../shared/classNames";
import { sectionIcons } from "./sectionIcons";
type SidebarItem = {
  label: string;
  href: string;
  disabled?: boolean;
  external?: boolean;
};
function externalNavHref(href: string): string {
  const path = spaPathFromBackendHref(href);
  if (/^https?:\/\//i.test(path)) return path;
  const base = spaAssetBase().replace(/\/$/, "");
  return `${base}${path.startsWith("/") ? path : `/${path}`}`;
}
type SidebarSection = {
  title: string;
  icon?: ReactNode;
  links: SidebarItem[];
};
function sessionNavigationToSidebar(nav: SessionPayload["navigation"]): SidebarSection[] {
  if (!Array.isArray(nav) || !nav.length) return [];
  return nav
    .map((sec) => ({
      title: `${sec.title || ""}`.trim() || "Menu",
      links: (sec.links || []).map((lnk) => {
        const href = spaPathFromBackendHref(lnk.href);
        // API Manual → Swagger; treat as full-page even if session omits ``external``.
        const docsPath = href.split("?")[0].replace(/\/$/, "") || "/";
        const external = Boolean(lnk.external) || docsPath === "/docs";
        return { label: lnk.label, href, external };
      }),
    }))
    .filter((s) => s.links.length > 0);
}
function firstAllowedNavHrefFromSidebar(nav: SidebarSection[]): string {
  for (const sec of nav) {
    for (const lnk of sec.links) {
      const h = `${lnk.href || ""}`.trim();
      if (h) return h;
    }
  }
  return "/";
}
function navSectionKey(section: SidebarSection, secIdx: number): string {
  return `${section.title}-${secIdx}`;
}
/** True if current pathname is the nav link, under it, or a common singular detail path (e.g. /modbus-templates → /modbus-template). */
function sidebarPathMatchesItem(itemHref: string, pathname: string, disabled?: boolean): boolean {
  if (disabled) return false;
  const raw = `${itemHref || ""}`.split("?")[0].trim() || "/";
  if (matchPath({ path: raw, end: true }, pathname)) return true;
  const p = raw.replace(/\/$/, "") || "/";
  const cur = `${pathname || ""}`.replace(/\/$/, "") || "/";
  if (p === "/" || cur === "/") return false;
  if (cur === p || cur.startsWith(`${p}/`)) return true;
  if (p.endsWith("s") && p.length > 2) {
    const singular = p.slice(0, -1);
    if (cur === singular || cur.startsWith(`${singular}/`)) return true;
  }
  return false;
}
function sidebarUseGroupLayout(section: SidebarSection): boolean {
  if (section.links.length > 1) return true;
  if (section.links.length === 1) {
    const only = section.links[0];
    return only.label.trim() !== section.title.trim();
  }
  return false;
}
export function Sidebar({
  open,
  showDesktop,
  onClose,
  onLogout,
  session,
}: {
  open: boolean;
  showDesktop: boolean;
  onClose: () => void;
  onLogout: () => void;
  session: SessionPayload;
}) {
  const location = useLocation();
  const nav = useMemo(() => sessionNavigationToSidebar(session.navigation), [session.navigation]);
  /** Logo / brand target: avoid sending restricted users to ``/`` when they only have other pages allowed. */
  const brandHref = useMemo(() => firstAllowedNavHrefFromSidebar(nav), [nav]);
  /** Exactly one sidebar link — show one clear row (no accordion / section-vs-label confusion). */
  const loneAccessiblePage = useMemo(() => {
    if (nav.length !== 1 || nav[0].links.length !== 1) return null;
    const secTitle = nav[0].title;
    const link = nav[0].links[0];
    return { secTitle, link };
  }, [nav]);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(() => new Set());
  // When the route lands on a child of a section, expand that section (same as navigating via the child link).
  // Does not run on header toggle alone, so click-to-collapse while staying on the page still works.
  useEffect(() => {
    setCollapsedGroups((prev) => {
      let next: Set<string> | null = null;
      nav.forEach((section, secIdx) => {
        if (!sidebarUseGroupLayout(section)) return;
        const gkey = navSectionKey(section, secIdx);
        const hasActive = section.links.some((item) =>
          sidebarPathMatchesItem(item.href, location.pathname, item.disabled),
        );
        if (hasActive && prev.has(gkey)) {
          if (!next) next = new Set(prev);
          next.delete(gkey);
        }
      });
      return next ?? prev;
    });
  }, [location.pathname, nav]);
  return (
    <>
      <div className={classNames("internal-sidebar-overlay", open && "is-open")} onClick={onClose} />
      <aside
        className={classNames(
          "internal-sidebar",
          showDesktop ? "show-desktop" : "hide-desktop",
          open && "is-open",
        )}
      >
        <div className="internal-sidebar-header">
          <Link to={brandHref} className="internal-brand" onClick={onClose}>
            <div className="internal-brand-icon">
              <img src="/klt-rms-logo.png" alt="KLT-RMS" className="internal-brand-logo" />
            </div>
            <div>
              <div className="internal-brand-title">KLT-RMS</div>
              <div className="internal-brand-subtitle">IoTCore</div>
            </div>
          </Link>
          <button type="button" className="internal-sidebar-close" onClick={onClose} aria-label="Close menu">
            <X className="h-5 w-5" />
          </button>
        </div>
        <nav className="internal-nav">
          {loneAccessiblePage ? (
            <div key="solo-allowed-page" className="internal-nav-section internal-nav-section--solo">
              <NavLink
                to={loneAccessiblePage.link.href}
                onClick={onClose}
                title={loneAccessiblePage.secTitle}
                className={({ isActive }) => classNames("internal-nav-parent", "internal-nav-parent--solo", isActive && "is-active")}
              >
                {sectionIcons[loneAccessiblePage.secTitle] || <ChevronRight className="h-4 w-4" />}
                <span>{loneAccessiblePage.link.label}</span>
              </NavLink>
            </div>
          ) : (
            nav.map((section, secIdx) => {
            const gkey = navSectionKey(section, secIdx);
            const useGroup = sidebarUseGroupLayout(section);
            const groupExpanded = !useGroup || !collapsedGroups.has(gkey);
            return (
              <div key={gkey} className="internal-nav-section">
                {!useGroup ? (
                  <NavLink
                    to={section.links[0].href}
                    onClick={onClose}
                    className={({ isActive }) => classNames("internal-nav-parent", isActive && "is-active")}
                  >
                    {section.icon || sectionIcons[section.title] || <ChevronRight className="h-4 w-4" />}
                    <span>{section.title}</span>
                  </NavLink>
                ) : (
                  <button
                    type="button"
                    className="internal-nav-parent"
                    aria-expanded={groupExpanded}
                    aria-controls={`nav-group-${gkey}`}
                    onClick={() => {
                      setCollapsedGroups((prev) => {
                        const next = new Set(prev);
                        if (next.has(gkey)) next.delete(gkey);
                        else next.add(gkey);
                        return next;
                      });
                    }}
                  >
                    {section.icon || sectionIcons[section.title] || <ChevronRight className="h-4 w-4" />}
                    <span>{section.title}</span>
                    <ChevronRight
                      className={classNames("internal-nav-chevron", !groupExpanded && "is-collapsed")}
                      aria-hidden
                    />
                  </button>
                )}
                {useGroup && (
                  <div
                    className={classNames("internal-nav-children", !groupExpanded && "is-collapsed")}
                    id={`nav-group-${gkey}`}
                  >
                    {section.links.map((item) =>
                      item.disabled ? (
                        <div
                          key={`${section.title}-${item.href}-${item.label}`}
                          className="internal-nav-child is-disabled"
                          title="Open this detail page from its parent list."
                        >
                          <span>{item.label}</span>
                          <span className="internal-nav-badge">detail</span>
                        </div>
                      ) : item.external ? (
                        <a
                          key={`${section.title}-${item.href}-${item.label}`}
                          href={externalNavHref(item.href)}
                          className="internal-nav-child"
                          onClick={onClose}
                        >
                          <span>{item.label}</span>
                        </a>
                      ) : (
                        <NavLink
                          key={`${section.title}-${item.href}-${item.label}`}
                          to={item.href}
                          onClick={onClose}
                          className={({ isActive }) =>
                            classNames("internal-nav-child", isActive && "is-active")
                          }
                        >
                          <span>{item.label}</span>
                        </NavLink>
                      ),
                    )}
                  </div>
                )}
              </div>
            );
          })
          )}
        </nav>
        <div className="internal-sidebar-footer">
          <button type="button" onClick={onLogout} className="internal-logout-button">
            Log Out
          </button>
        </div>
      </aside>
    </>
  );
}
